import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng-lts/api';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { VehicleValidationHelper } from '../../helpers/vehicle-validation.helper';
import { FleetChecklistStoreService } from '../../store/fleet-checklist-store.service';
import { VehicleValidationEditorService } from '../../services/vehicle-validation-editor.service';
import { DynamicField } from '../../models/dynamic-field';
import { VehicleValidation, VehicleValidationStatus } from '../../models/vehicle-validation';
import { ValidationCriterion } from '../../models/validation-criterion';
import { UnitTransmissionStatusService } from 'src/app/units/services/unit-transmission-status.service';
import { convertSecondsToTimeString } from 'src/app/vehicles/components/content-panel-vehicles/helpers/convert-seconds-to-time-string';

@Component({
  selector: 'app-vehicle-checklist-table',
  templateUrl: './vehicle-checklist-table.component.html',
  styleUrls: ['./vehicle-checklist-table.component.scss']
})
export class VehicleChecklistTableComponent implements OnInit, OnDestroy {
  autoMarkingActive = false;

  items: MenuItem[] = [];
  allCompleted = false;

  private transmissionSubscription?: Subscription;
  private storeSubscription?: Subscription;
  private autoMarkingSubscription?: Subscription;

  constructor(
    public store: FleetChecklistStoreService,
    public vehicleEditor: VehicleValidationEditorService,
    private transmissionService: UnitTransmissionStatusService,
  ) { }

  statusClasses: Record<string, string> = {
    '10': 'status-active',
    '11': 'status-inactive',
    '8': 'status-warning'
  };
  allOptions :  Record<string, string[]> = {
    ruta: [
      'AQP - CUS',
      'AQP - TYA',
      'CUS - AQP',
      'CUS - TYA',
      'TYA - AQP',
      'TYA - CUS'
    ]
  };

  fieldSuggestions: Record<string, string[]> = {};
  vehicles$ = this.vehicleEditor.filteredVehicles$;
  fleetChecklist$ = this.store.fleetChecklist$;

  getMenuItems() {
    const vehicle = this.vehicleEditor.getSelectedVehicle();

    if(!vehicle) return [];

    return [
      {
        label: 'Subir evidencia',
        icon: 'pi pi-upload',
        command: () => this.uploadEvidence()
      },
      ...(vehicle.status != VehicleValidationStatus.completed &&
      vehicle.status != VehicleValidationStatus.inReview &&
      vehicle.status != VehicleValidationStatus.observation
        ? [{
            label: 'Limpiar inspección',
            icon: 'pi pi-circle-off',
            command: () => this.vehicleEditor.resetVehicleCriteria(vehicle.id)
          }]
        : []),
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        command: () => this.deleteVehicle(vehicle)
      }
    ];
  }

  ngOnInit(): void {
    this.vehicles$.subscribe(vehicles => {
      this.updateAllCompleted(vehicles);
    });
  }

  filterSuggestions(event: any, field: DynamicField) {
    const query = (event.query || '').toLowerCase();

    this.fieldSuggestions[field.id] =
      this.allOptions[field.id]?.filter(option =>
        option.toLowerCase().includes(query)
      ) || [];
  }
  openSuggestions(event: any, field: any) {
    const options = this.allOptions[field.id] || [];

    this.fieldSuggestions[field.id] = [...options];

    // fuerza apertura del panel
    setTimeout(() => {
      event?.target?.dispatchEvent(new Event('input'));
    });
  }

  toggleMenu(event: MouseEvent, vehicle: VehicleValidation, menu: any): void {
    event.stopPropagation();
    if (this.store.isEditable()) {
      this.vehicleEditor.setSelectedVehicle(vehicle);
      this.items = this.getMenuItems();
      menu.toggle(event);
    }
  }

  deleteVehicle(vehicle: VehicleValidation | null = null): void {
    if (vehicle) {
      this.removeVehicle(vehicle);
    }
  }

  ngOnDestroy(): void {
    if (this.transmissionSubscription) {
      this.transmissionSubscription.unsubscribe();
    }
    if (this.storeSubscription) {
      this.storeSubscription.unsubscribe();
    }
    if (this.autoMarkingSubscription) {
      this.autoMarkingSubscription.unsubscribe();
    }
  }


  isPriorityFieldLocked(vehicle: VehicleValidation, field: DynamicField): boolean {
    const isLocked =
      vehicle.status == 'inReview' ||
      vehicle.status == 'completed' ||
      vehicle.status == 'observation';

    const isPriorityField =
      field.id === 'programada' || field.id === 'ruta';

    const hasNoValue =
      !vehicle.fields[field.id]?.value ||
      vehicle.fields[field.id]?.value.toString().trim() === '';

    return isLocked && isPriorityField && hasNoValue;
  }


  onFieldValueChange(vehicleId: string, fieldId: string, value: any): void {
    this.vehicleEditor.updateFieldValue(vehicleId, fieldId, value);
  }

  onObservationsChange(vehicleId: string, value: string): void {
    this.vehicleEditor.updateObservations(vehicleId, value);
  }

  getCriterionStateName(
    vehicle: VehicleValidation,
    criterion: ValidationCriterion
  ): string {
    const selectedCrit = vehicle.criteria[criterion.id];
    if (!selectedCrit?.selectedStateId) return '...';

    const state = criterion.states?.find(
      s => s.id === selectedCrit.selectedStateId
    );
    return state?.name ?? '...';
  }
  getCriterionStateClass(vehicle: VehicleValidation, criterion: ValidationCriterion): string {
    const selected = vehicle.criteria[criterion.id];
    if (!selected?.selectedStateId) return 'status-pending';
    return this.statusClasses[selected.selectedStateId] ?? 'status-inactive';
  }

  toggleCriterionState(vehicle: VehicleValidation, criterion: ValidationCriterion): void {
    if (!this.store.isInitialized() || this.store.getStatus() !== 'pending') return;
    if (!criterion || !criterion.states || criterion.states.length === 0) return;
    this.vehicleEditor.toggleCriterionState(vehicle.id, criterion.id, criterion.states, criterion.name);
  }

  hasTransmissionState(vehicle: VehicleValidation, criterion: ValidationCriterion): boolean {
    if (!criterion?.name || !vehicle?.parameters?.transmission) return false;
    const name = criterion.name.toLowerCase();
    if (name.includes('gps')) {
      return !!vehicle.parameters.transmission['gps'];
    }
    if (name.includes('cipia')) {
      return !!vehicle.parameters.transmission['cipia'];
    }
    return false;
  }

  getTransmissionData(vehicle: VehicleValidation, criterion: ValidationCriterion) {
    if (!criterion?.name || !vehicle?.parameters?.transmission) return null;
    const name = criterion.name.toLowerCase();
    let tracker: 'gps' | 'cipia' | null = null;
    if (name.includes('gps')) {
      tracker = 'gps';
    } else if (name.includes('cipia')) {
      tracker = 'cipia';
    }
    if (!tracker) return null;

    const data = vehicle.parameters.transmission[tracker];
    if (!data) return null;

    const statusInfo = this.transmissionService.getTransmissionInfoById(data.statusId);
    let timeStr = '';
    if (data.elapsedTime !== undefined && data.elapsedTime !== null) {
      timeStr = convertSecondsToTimeString(data.elapsedTime, { shortFormat: false });
    }

    return {
      statusName: statusInfo?.name || 'No definido',
      statusColor: statusInfo?.color || '#999999',
      elapsedTime: timeStr,
      lastStartTime: data.lastStartTime
    };
  }

  hasEquipmentStatusState(vehicle: VehicleValidation, criterion: ValidationCriterion): boolean {
    if (!criterion?.name || !vehicle?.parameters?.equipmentStatus) return false;
    const name = criterion.name.toLowerCase();
    if (name.includes('adas')) {
      return !!vehicle.parameters.equipmentStatus['adas'];
    }
    if (name.includes('alco')) {
      return !!vehicle.parameters.equipmentStatus['alcohol'];
    }
    return false;
  }

  getEquipmentData(vehicle: VehicleValidation, criterion: ValidationCriterion) {
    if (!criterion?.name || !vehicle?.parameters?.equipmentStatus) return null;
    const name = criterion.name.toLowerCase();
    let eqKey: 'adas' | 'alcohol' | null = null;
    if (name.includes('adas')) {
      eqKey = 'adas';
    } else if (name.includes('alco')) {
      eqKey = 'alcohol';
    }
    if (!eqKey) return null;

    const data = vehicle.parameters.equipmentStatus[eqKey];
    if (!data) return null;

    return {
      name: eqKey === 'adas' ? 'ADAS' : 'Alcoholímetro',
      lastEventDate: data.lastEventDate,
      lastEventName: data.lastEventName
    };
  }

  hasEvidences(vehicle: VehicleValidation, criterion: ValidationCriterion): boolean {
    if (!criterion?.name || !vehicle?.parameters?.evidences) return false;
    const name = criterion.name.toLowerCase();
    let isCamera = name.includes('maras');
    if (!isCamera) return false;
    let evidences = vehicle.parameters.evidences;
    if (!evidences || evidences.length == 0) return false;
    return true;
  }

  trackByVehicleId(index: number, vehicle: VehicleValidation): string {
    return vehicle.id;
  }

  trackByFieldId(index: number, field: DynamicField): string {
    return field.id;
  }

  trackByCriterionId(index: number, criterion: ValidationCriterion): string {
    return criterion.id;
  }
  updateAllCompleted(vehicles: VehicleValidation[]) {
    const validVehicles = vehicles.filter(v => this.isVehicleComplete(v));
    if (validVehicles.length === 0) {
      this.allCompleted = false;
      return;
    }
    this.allCompleted = validVehicles.every(v =>
      ['completed', 'inReview', 'observation'].includes(v.status || '')
    );
  }

  // ACCIONES DEL VEHICULO
  uploadEvidence(vehicle: VehicleValidation | null = null): void {
    if (vehicle) {
      this.vehicleEditor.openEvidenceModal(vehicle);
    } else {
      const v = this.vehicleEditor.getSelectedVehicle();
      if (v) {
        this.vehicleEditor.openEvidenceModal(v);
      }
    }
  }

  removeVehicle(vehicle: VehicleValidation): void {
    Swal.fire({
      title: '¿Eliminar este vehículo del grupo?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.vehicleEditor.removeVehicle(vehicle);
      }
    });
  }

  async toggleAllCompletion(checked: boolean): Promise<void> {
    const vehicles = this.vehicleEditor.vehicles;

    for (const vehicle of vehicles) {
      const disabled = !this.store.isEditable() || !this.isVehicleComplete(vehicle);

      if (disabled) {
        continue;
      }

      const newStatus = checked
        ? VehicleValidationHelper.getValidationStatusByVehicle(vehicle)
        : VehicleValidationStatus.incompleted;

      await this.vehicleEditor.toggleVehicleValidationStatus(
        vehicle.id,
        newStatus
      );
    }
  }
  async toggleCompletion(vehicle: VehicleValidation): Promise<void> {
    let newStatus: VehicleValidationStatus;
    switch (vehicle.status) {
      case VehicleValidationStatus.inReview:
        newStatus = VehicleValidationStatus.incompleted;
        break;

      case VehicleValidationStatus.completed:
        newStatus = VehicleValidationStatus.incompleted;
        break;

      case VehicleValidationStatus.observation:
        newStatus = VehicleValidationStatus.incompleted;
        break;

      default:
        newStatus = VehicleValidationHelper.getValidationStatusByVehicle(vehicle);
        break;
    }
    this.vehicleEditor.toggleVehicleValidationStatus(vehicle.id, newStatus);
    console.log('toggleCompletion', vehicle, newStatus);
    // if(newStatus != VehicleValidationStatus.incompleted && (!vehicle.fields['programada']?.value || !vehicle.fields['ruta']?.value)) {
    //   await Swal.fire({
    //     toast: true,
    //     icon: 'warning',
    //     title: 'El vehículo no tiene hora programada o ruta.',
    //     position: 'top-start', // Arriba izquierda
    //     showConfirmButton: false,
    //     timer: 3000,
    //     timerProgressBar: true
    //   });
    // }
  }

  isVehicleComplete(vehicle: VehicleValidation): boolean {
    let type = this.store.getType() || '';
    return VehicleValidationHelper.isVehicleComplete(vehicle, type);
  }

  isCameraCriterion(criterion: any): boolean {
    if (!criterion || !criterion.name) return false;
    const name = criterion.name.toLowerCase();
    return name.includes('mara') || name.includes('camera');
  }


  isPrincipalCriterion(criterion: any): boolean {
    if (!criterion || !criterion.name) return false;
    const name = criterion.name.toLowerCase();
    return name.includes('gps') || name.includes('mara') || name.includes('cipia') || name.includes('copia');
  }
}
